import type { CategoryColor } from "./types";

/**
 * Curated palette for known categories.
 * Each entry is hand-picked for maximum visual distinction.
 */
/**
 * Canonical display order for categories.
 * This fixed order is used across all charts so bars/rows stay consistent.
 */
export const CATEGORY_ORDER = [
  "Akademisk mestring",
  "Fysisk helse",
  "Egenomsorg",
  "Kreativ utfoldelse",
  "Sosial kompetanse",
  "Ansvar hjemme",
  "Selvstendighet",
  "Personlig utvikling",
  "Annet",
] as const;

const CURATED_PALETTE: Record<string, { h: number; s: number; l: number }> = {
  "Akademisk mestring": { h: 217, s: 72, l: 52 },   // blue (brand-adjacent)
  "Fysisk helse":       { h: 142, s: 55, l: 45 },   // green
  "Egenomsorg":         { h: 25,  s: 80, l: 50 },   // warm orange
  "Kreativ utfoldelse": { h: 280, s: 60, l: 55 },   // purple
  "Sosial kompetanse":  { h: 175, s: 60, l: 42 },   // teal
  "Ansvar hjemme":      { h: 45,  s: 75, l: 48 },   // amber/gold
  "Selvstendighet":     { h: 350, s: 65, l: 50 },   // rose/red
  "Personlig utvikling":{ h: 310, s: 55, l: 52 },   // magenta/pink
  "Annet":              { h: 200, s: 15, l: 55 },   // neutral grey-blue
};

function hashString(str: string): number {
  let h = 0;
  for (let i = 0; i < str.length; i++) {
    h = ((h << 5) - h + str.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
}

/**
 * Build a deterministic, collision-free color map for categories.
 * Known categories get curated colors; unknown ones fall back to hash-based
 * with collision avoidance against already-used hues.
 */
export function buildCategoryColorMap(
  categories: string[]
): Record<string, CategoryColor> {
  const usedHues: number[] = [];
  const map: Record<string, CategoryColor> = {};
  const step = 24;
  const minDelta = 20;

  for (const cat of categories) {
    const curated = CURATED_PALETTE[cat];
    if (curated) {
      usedHues.push(curated.h);
      map[cat] = {
        stroke: `hsl(${curated.h}, ${curated.s}%, ${curated.l}%)`,
        fill: `hsla(${curated.h}, ${curated.s}%, ${curated.l}%, 0.15)`,
      };
    } else {
      // Fallback: hash-based with collision avoidance
      let hue = (217 + (hashString(cat) % 320)) % 360;
      hue = Math.round(hue / step) * step;
      let attempts = 0;
      while (
        attempts < 15 &&
        usedHues.some(
          (h) =>
            Math.min(Math.abs(hue - h), 360 - Math.abs(hue - h)) < minDelta
        )
      ) {
        hue = (hue + step) % 360;
        attempts++;
      }
      usedHues.push(hue);
      const sat = 58 + (hashString(cat) % 3) * 4;
      const lit = 48 + (hashString(cat) % 3) * 3;
      map[cat] = {
        stroke: `hsl(${hue}, ${sat}%, ${lit}%)`,
        fill: `hsla(${hue}, ${sat}%, ${lit}%, 0.15)`,
      };
    }
  }
  return map;
}

/** Get an hsla version of a category's stroke color at a given alpha */
export function categoryStrokeAlpha(stroke: string, alpha: number): string {
  const match = stroke.match(/hsl\((\d+),\s*(\d+)%,\s*(\d+)%\)/);
  if (!match) return stroke;
  return `hsla(${match[1]}, ${match[2]}%, ${match[3]}%, ${alpha})`;
}
